// Link na bio: pagina propria do tenant, separada da vitrine (/<slug>/bio no
// Worker publico). Perfil, tema, estilo dos botoes, redes sociais e links
// ordenaveis com contagem de cliques, com previa mobile ao vivo.

import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ExternalLink, Eye, Loader2, Lock, Sparkles } from "lucide-react";
import { DEFAULT_BIO_SETTINGS, type BioPageSettings } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { BioPreview } from "@/components/bio/BioPreview";
import { BioLinksEditor } from "@/components/bio/BioLinksEditor";
import { BioProfileFields, BioSocialFields, BioStyleFields } from "@/components/bio/BioAppearance";
import { useBioLinks, useBioPage, useSaveBioPage } from "@/lib/hooks/useBioPage";
import { useStorefront } from "@/lib/hooks/useStorefront";

export const BIO_PUBLIC_BASE = "https://loja.lucralink.com.br";

export function bioPublicUrl(slug: string | null): string | null {
  return slug ? `${BIO_PUBLIC_BASE}/${slug}/bio` : null;
}

function Section({ title, description, children }: { title: string; description?: string; children: JSX.Element }): JSX.Element {
  return (
    <Card variant="raised">
      <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="pt-5">{children}</CardContent>
    </Card>
  );
}

function UpgradeBanner(): JSX.Element {
  return (
    <Card variant="ghost" className="border-[hsl(var(--gold)/0.35)]">
      <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
        <p className="flex items-center gap-2 text-sm text-foreground">
          <Lock size={14} /> O link na bio faz parte do plano Pro.
        </p>
        <Button asChild variant="gold" size="sm">
          <Link to="/app/plans">
            <Sparkles size={14} /> Ver planos
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

interface PublishBarProps {
  readonly tenantSlug: string | null;
  readonly isPublished: boolean;
  readonly disabled: boolean;
  readonly isPending: boolean;
  readonly onSave: (status: "draft" | "published", slug: string | null) => void;
}

function PublishBar({ tenantSlug, isPublished, disabled, isPending, onSave }: PublishBarProps): JSX.Element {
  const [slug, setSlug] = useState("");
  const url = bioPublicUrl(tenantSlug);
  return (
    <div className="space-y-3">
      {!tenantSlug && (
        <div className="space-y-1.5">
          <Label htmlFor="bio-slug" variant="field">Endereço da sua página</Label>
          <Input id="bio-slug" placeholder="seu-nome" value={slug} onChange={(e) => setSlug(e.target.value)} />
          <p className="text-xs text-muted-foreground">Fica em loja.lucralink.com.br/{slug || "seu-nome"}/bio (o mesmo endereço da vitrine).</p>
        </div>
      )}
      {url && (
        <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm text-primary hover:underline">
          <ExternalLink size={13} /> {url}
        </a>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" disabled={disabled || isPending} onClick={() => onSave("published", slug.trim() || null)}>
          {isPending && <Loader2 size={14} className="animate-spin" />}
          {isPublished ? "Salvar e manter publicada" : "Publicar página"}
        </Button>
        <Button type="button" variant="secondary" disabled={disabled || isPending} onClick={() => onSave("draft", slug.trim() || null)}>
          {isPublished ? "Despublicar" : "Salvar rascunho"}
        </Button>
      </div>
    </div>
  );
}

export function BioPage(): JSX.Element {
  const { data: storefront } = useStorefront();
  const { data: page, isError: pageError } = useBioPage();
  const { data: links = [], isError: linksError } = useBioLinks();
  const save = useSaveBioPage();
  const [settings, setSettings] = useState<BioPageSettings>(DEFAULT_BIO_SETTINGS);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);
  useEffect(() => {
    if (page) setSettings(page.settings);
  }, [page]);
  const isPro = storefront?.hasBioLink ?? false;
  const notify = (text: string, ok: boolean): void => setMessage({ text, ok });

  function handleSave(status: "draft" | "published", slug: string | null): void {
    save.mutate(
      { settings, status, slug },
      {
        onSuccess: () => notify(status === "published" ? "Página publicada. Atualiza em até 1 minuto." : "Rascunho salvo.", true),
        onError: (e) => notify(e.message, false),
      },
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      <PageHeader title="Link na bio" subtitle="Sua página de links, separada da vitrine: perfil, botões e redes sociais." />
      {!isPro && <UpgradeBanner />}
      {(pageError || linksError) && <p role="alert" className="text-sm text-destructive">Não foi possível carregar sua página de bio.</p>}
      {message && <p role={message.ok ? "status" : "alert"} className={message.ok ? "text-sm text-primary" : "text-sm text-destructive"}>{message.text}</p>}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <fieldset disabled={!isPro} className="min-w-0 space-y-6">
          <Section title="Perfil">
            <BioProfileFields settings={settings} onChange={setSettings} />
          </Section>
          <Section title="Aparência" description="Tema da página e formato dos botões.">
            <BioStyleFields settings={settings} onChange={setSettings} />
          </Section>
          <Section title="Links" description="Qualquer endereço https: loja, grupo, WhatsApp, vídeo.">
            <BioLinksEditor links={links} disabled={!isPro} onMessage={notify} />
          </Section>
          <Section title="Redes sociais" description="Ícones exibidos abaixo do perfil.">
            <BioSocialFields settings={settings} onChange={setSettings} />
          </Section>
          <Section title="Publicação">
            <PublishBar tenantSlug={storefront?.slug ?? null} isPublished={page?.status === "published"} disabled={!isPro} isPending={save.isPending} onSave={handleSave} />
          </Section>
        </fieldset>
        <div className="space-y-3 xl:sticky xl:top-6 xl:self-start">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            <Eye size={13} /> Prévia no celular
          </div>
          <BioPreview settings={settings} links={links} />
        </div>
      </div>
    </div>
  );
}
